import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { travelerApi } from '../services/travelerApi';
import TravelerForm from '../components/traveler/TravelerForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { ToastContainer } from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import { LayoutGrid, List, Users, Pencil, Pause, Play, Trash2, Plus } from 'lucide-react';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';
import {
  Traveler,
  CreateTravelerRequest,
  UpdateTravelerRequest,
  PersonType,
  PersonTypes,
  getPersonTypeInfo,
  formatLicenseClasses,
  isLicenseExpired,
  isLicenseExpiringSoon
} from '../types/traveler';

/**
 * Travelers page component with unified person management
 */
const TravelersPage: React.FC = () => {
  const { t } = useTranslation(['common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [travelers, setTravelers] = useState<Traveler[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'list' | 'cards'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Initialize selectedPersonType from URL parameter
  const typeParam = searchParams.get('type') as PersonType | null;
  const [selectedPersonType, setSelectedPersonType] = useState<PersonType | 'all'>(
    typeParam && Object.values(PersonTypes).includes(typeParam) ? typeParam : 'all'
  );

  // Form and dialog states
  const [showForm, setShowForm] = useState(false);
  const [editingTraveler, setEditingTraveler] = useState<Traveler | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [travelerToDelete, setTravelerToDelete] = useState<Traveler | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Toast notifications
  const { toasts, removeToast, showSuccess, showError } = useToast();

  useEffect(() => {
    loadTravelers();
  }, [showInactive, selectedPersonType]);

  // Update URL when person type changes
  useEffect(() => {
    if (selectedPersonType === 'all') {
      searchParams.delete('type');
    } else {
      searchParams.set('type', selectedPersonType);
    }
    setSearchParams(searchParams, { replace: true });
  }, [selectedPersonType, searchParams, setSearchParams]);

  const loadTravelers = async () => {
    try {
      setLoading(true);
      setError(null);
      const personType = selectedPersonType === 'all' ? undefined : selectedPersonType;
      const data = await travelerApi.getAllTravelers(showInactive, personType);
      setTravelers(data);
    } catch (error) {
      console.error('Error loading travelers:', error);
      setError(t('common:travelers.loadError'));
      showError(t('common:travelers.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTraveler = async (data: CreateTravelerRequest) => {
    try {
      setFormLoading(true);
      await travelerApi.createTraveler(data);
      showSuccess(t('common:travelers.createSuccess'));
      setShowForm(false);
      await loadTravelers();
    } catch (error) {
      console.error('Error creating traveler:', error);
      showError(t('common:travelers.createError'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateTraveler = async (data: UpdateTravelerRequest) => {
    if (!editingTraveler) return;

    try {
      setFormLoading(true);
      await travelerApi.updateTraveler(editingTraveler.id, data);
      showSuccess(t('common:travelers.updateSuccess'));
      setShowForm(false);
      setEditingTraveler(null);
      await loadTravelers();
    } catch (error) {
      console.error('Error updating traveler:', error);
      showError(t('common:travelers.updateError'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTraveler = (traveler: Traveler) => {
    setTravelerToDelete(traveler);
    setShowDeleteDialog(true);
  };

  const confirmDeleteTraveler = async () => {
    if (!travelerToDelete) return;

    try {
      setDeleteLoading(true);
      await travelerApi.deleteTraveler(travelerToDelete.id);
      showSuccess(t('common:travelers.deleteSuccess'));
      setShowDeleteDialog(false);
      setTravelerToDelete(null);
      await loadTravelers();
    } catch (error) {
      console.error('Error deleting traveler:', error);
      showError(t('common:travelers.deleteError'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleActive = async (traveler: Traveler) => {
    try {
      await travelerApi.toggleTravelerActive(traveler.id);
      const statusKey = traveler.isActive ? 'inactive' : 'active';
      showSuccess(`${t('common:travelers.title')} ${t(`common:${statusKey}`)}`);
      await loadTravelers();
    } catch (error) {
      console.error('Error toggling traveler status:', error);
      showError(t('common:error'));
    }
  };

  const handleEditTraveler = (traveler: Traveler) => {
    setEditingTraveler(traveler);
    setShowForm(true);
  };

  // Filter travelers based on search term
  const filteredTravelers = travelers.filter(traveler =>
    traveler.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    traveler.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    traveler.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    traveler.personTypeDisplay.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (traveler.notes && traveler.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
    formatLicenseClasses(traveler.licenseClasses).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Statistics
  const stats = {
    total: filteredTravelers.length,
    active: travelers.filter(t => t.isActive).length,
    drivers: travelers.filter(t => t.personType === PersonTypes.DRIVER).length,
    players: travelers.filter(t => t.personType === PersonTypes.PLAYER).length,
    coaches: travelers.filter(t => t.personType === PersonTypes.COACH).length,
    staff: travelers.filter(t => t.personType === PersonTypes.STAFF).length,
    expiredLicenses: travelers.filter(t => t.isDriver && isLicenseExpired(t.licenseExpiry)).length,
    expiringSoon: travelers.filter(t => t.isDriver && isLicenseExpiringSoon(t.licenseExpiry)).length
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading">
          <div className="page-spinner"></div>
          <p>{t('common:travelers.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={() => setShowForm(true)} title={t('common:travelers.addTraveler')}>
            <Plus size={14} /> Neu
          </button>
          <label className="page-checkbox-label">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            {t('common:travelers.showInactive')}
          </label>
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + Filters + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder={t('common:travelers.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="page-filter-select"
            value={selectedPersonType}
            onChange={(e) => setSelectedPersonType(e.target.value as PersonType | 'all')}
          >
            <option value="all">{t('common:travelers.allTypes')}</option>
            {Object.values(PersonTypes).map(type => {
              const info = getPersonTypeInfo(type);
              return (
                <option key={type} value={type}>
                  {info.icon} {info.label}
                </option>
              );
            })}
          </select>
          <div className="page-controls-spacer" />
          <span className="page-record-count">
            {stats.total} {t('common:travelers.stats.total')} · {stats.active} {t('common:travelers.stats.active')} · {stats.drivers} {t('common:travelers.stats.drivers')}
            {stats.expiredLicenses > 0 && <> · ⚠️ {stats.expiredLicenses} {t('common:travelers.stats.expiredLicenses')}</>}
          </span>
        </div>
      </div>

      {error && (
        <div className="page-error">
          <p>{error}</p>
          <button onClick={loadTravelers}>{t('common:refresh')}</button>
        </div>
      )}

      {!loading && filteredTravelers.length === 0 && (
        <div className="page-empty-state">
          <Users size={32} />
          <h3>{t('common:travelers.noTravelers')}</h3>
          <p>{t('common:noData')}</p>
          {!searchTerm && (
            <button className="page-toggle-btn page-btn-create" onClick={() => setShowForm(true)}>
              <Plus size={14} /> {t('common:travelers.addTraveler')}
            </button>
          )}
        </div>
      )}

      {/* Content */}
      {!loading && filteredTravelers.length > 0 && (
        <>
          {viewMode === 'cards' ? (
            <div className="page-cards-grid">
              {filteredTravelers.map((traveler) => {
                const personTypeInfo = getPersonTypeInfo(traveler.personType);
                const licenseExpired = traveler.isDriver && isLicenseExpired(traveler.licenseExpiry);
                const licenseExpiringSoon = traveler.isDriver && isLicenseExpiringSoon(traveler.licenseExpiry);

                return (
                  <div key={traveler.id} className="page-card">
                    <div className="page-card-header">
                      <h3>{personTypeInfo.icon} {traveler.fullName}</h3>
                      <div className="page-card-badges">
                        <span className="page-badge" style={{ backgroundColor: '#6c757d' }}>{traveler.personTypeDisplay}</span>
                        <span className="page-badge" style={{ backgroundColor: traveler.isActive ? '#28a745' : '#dc3545' }}>
                          {traveler.isActive ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </div>
                    </div>
                    <div className="page-card-body">
                      {traveler.isDriver && (
                        <>
                          <div><strong>Führerschein:</strong> {formatLicenseClasses(traveler.licenseClasses)}
                            {licenseExpired && <span className="page-badge" style={{ backgroundColor: '#dc3545', marginLeft: 6 }}>Abgelaufen</span>}
                            {!licenseExpired && licenseExpiringSoon && <span className="page-badge" style={{ backgroundColor: '#ffc107', color: '#000', marginLeft: 6 }}>Läuft ab</span>}
                          </div>
                          {traveler.licenseExpiry && (
                            <div><strong>Gültig bis:</strong> {new Date(traveler.licenseExpiry).toLocaleDateString('de-DE')}</div>
                          )}
                        </>
                      )}
                      {traveler.notes && <p>{traveler.notes}</p>}
                    </div>
                    <div className="page-card-footer">
                      <span></span>
                      <div className="page-card-actions">
                        <button className="page-btn-icon edit" onClick={() => handleEditTraveler(traveler)} title="Bearbeiten"><Pencil size={14} /></button>
                        <button className="page-btn-icon" onClick={() => handleToggleActive(traveler)} title={traveler.isActive ? 'Deaktivieren' : 'Aktivieren'}>
                          {traveler.isActive ? <Pause size={14} /> : <Play size={14} />}
                        </button>
                        <button className="page-btn-icon delete" onClick={() => handleDeleteTraveler(traveler)} title="Löschen"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="page-data-panel">
              <table className="page-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Typ</th>
                    <th>Führerschein</th>
                    <th>Gültig bis</th>
                    <th>Status</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTravelers.map((traveler) => {
                    const personTypeInfo = getPersonTypeInfo(traveler.personType);
                    const licenseExpired = traveler.isDriver && isLicenseExpired(traveler.licenseExpiry);
                    const licenseExpiringSoon = traveler.isDriver && isLicenseExpiringSoon(traveler.licenseExpiry);

                    return (
                      <tr key={traveler.id}>
                        <td>{personTypeInfo.icon} {traveler.fullName}{traveler.notes && <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{traveler.notes}</div>}</td>
                        <td><span className="page-badge" style={{ backgroundColor: '#6c757d' }}>{traveler.personTypeDisplay}</span></td>
                        <td>
                          {traveler.isDriver ? (
                            <>{formatLicenseClasses(traveler.licenseClasses)}
                              {licenseExpired && <span className="page-badge" style={{ backgroundColor: '#dc3545', marginLeft: 4 }}>Abgelaufen</span>}
                              {!licenseExpired && licenseExpiringSoon && <span className="page-badge" style={{ backgroundColor: '#ffc107', color: '#000', marginLeft: 4 }}>Läuft ab</span>}
                            </>
                          ) : '-'}
                        </td>
                        <td>{traveler.isDriver && traveler.licenseExpiry ? new Date(traveler.licenseExpiry).toLocaleDateString('de-DE') : '-'}</td>
                        <td>
                          <span className="page-badge" style={{ backgroundColor: traveler.isActive ? '#28a745' : '#dc3545' }}>
                            {traveler.isActive ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td>
                          <div className="page-table-actions">
                            <button className="page-btn-icon edit" onClick={() => handleEditTraveler(traveler)} title="Bearbeiten"><Pencil size={14} /></button>
                            <button className="page-btn-icon" onClick={() => handleToggleActive(traveler)} title={traveler.isActive ? 'Deaktivieren' : 'Aktivieren'}>
                              {traveler.isActive ? <Pause size={14} /> : <Play size={14} />}
                            </button>
                            <button className="page-btn-icon delete" onClick={() => handleDeleteTraveler(traveler)} title="Löschen"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      
      {/* Traveler Form Modal */}
      {showForm && (
        <TravelerForm
          traveler={editingTraveler || undefined}
          onSave={editingTraveler ? 
            (data: CreateTravelerRequest | UpdateTravelerRequest) => handleUpdateTraveler(data as UpdateTravelerRequest) : 
            (data: CreateTravelerRequest | UpdateTravelerRequest) => handleCreateTraveler(data as CreateTravelerRequest)
          }
          onCancel={() => {
            setShowForm(false);
            setEditingTraveler(null);
          }}
          loading={formLoading}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title={t('common:travelers.deleteTraveler')}
        message={`${t('common:travelers.deleteConfirm')} "${travelerToDelete?.fullName}"`}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        onConfirm={confirmDeleteTraveler}
        onCancel={() => {
          setShowDeleteDialog(false);
          setTravelerToDelete(null);
        }}
        loading={deleteLoading}
        type="danger"
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default TravelersPage;
